import React, { useState, useCallback, forwardRef, useImperativeHandle, useEffect } from 'react';
import styled from 'styled-components';
import { CreditException } from '@indigo-ag/schema';
import { Input, Select, IconButton } from '@indigo-ag/component-module';
import { faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import i18next from 'i18next';
import { creditExceptionsOptions, text } from 'shared';
import SelectedExceptionsList from './SelectedExceptionsList';
import { getExceptionText } from 'shared/utils/creditExceptions.utils';

export interface CreditExceptionsFormHandle {
  validateOtherException: (value: string) => boolean;
  addError: (error: string) => void;
  clearErrors: () => void;
  hasOtherExceptionSelected: () => boolean;
}

interface CreditExceptionsFormProps {
  newExceptions: string[];
  setNewExceptions: (exceptions: string[]) => void;
  inputValue: string;
  setInputValue: (value: string) => void;
  alreadyAdded?: string[];
}

interface Errors {
  conditionals?: string;
  other?: string;
  alreadyAdded?: string;
}

type SelectedException = CreditException | string;

const FormRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 1rem;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const ExceptionSelect = styled(Select)`
  width: 100%;
`;

const CreditExceptionsForm = forwardRef<CreditExceptionsFormHandle, CreditExceptionsFormProps>(
  ({ newExceptions, setNewExceptions, inputValue, setInputValue, alreadyAdded = [] }, ref) => {
    const [errors, setErrors] = useState<Errors>({});
    const [selectedException, setSelectedException] = useState<SelectedException | null>(null);
    const [exceptionsList, setExceptionsList] = useState<string[]>(
      newExceptions.length > 0 ? newExceptions : alreadyAdded
    );
    const [otherExceptionSelected, setOtherExceptionSelected] = useState(false);

    useEffect(() => {
      setNewExceptions(exceptionsList.map(exception => exception as CreditException));
    }, [exceptionsList, setNewExceptions]);

    const validateOtherException = useCallback((value: string) => {
      if (value.length === 0) {
        setErrors(current => ({ ...current, other: i18next.t('creditExceptions-otherRequired') }));
        return false;
      }

      if (value.length < 3) {
        setErrors(current => ({
          ...current,
          other: i18next.t('creditExceptions-minLengthOtherException', { minLength: 3 })
        }));
        return false;
      }

      setErrors(current => ({ ...current, other: undefined }));
      return true;
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        validateOtherException,
        addError: (error: string) => setErrors(current => ({ ...current, other: error })),
        clearErrors: () => setErrors({}),
        hasOtherExceptionSelected: () => otherExceptionSelected
      }),
      [validateOtherException, otherExceptionSelected]
    );

    const handleAddException = () => {
      if (otherExceptionSelected && !validateOtherException(inputValue)) return;

      if (!selectedException) return;

      const selected = exceptionsList.find(item => item === selectedException);
      if (selected) return;

      const newException = otherExceptionSelected ? inputValue : selectedException;

      setExceptionsList(current => {
        const newExceptions = [...current, newException];
        return newExceptions.sort((a, b) => getExceptionText(a).localeCompare(getExceptionText(b)));
      });
      setSelectedException(null);
      setInputValue('');
      setOtherExceptionSelected(false);
    };

    const handleDeleteException = (exception: string) => {
      setExceptionsList(current => current.filter(item => item !== exception));
    };

    return (
      <FormContainer id="credit-exceptions-form">
        <FormRow>
          <ExceptionSelect
            name="exceptions"
            label={i18next.t('creditCollateralExceptions-label')}
            value={selectedException}
            placeholder={i18next.t('creditExceptions-selectPlaceholder')}
            options={creditExceptionsOptions}
            onChange={e => {
              const selected = e.target.value as CreditException;
              setSelectedException(selected);
              setOtherExceptionSelected(selected === CreditException.OTHER);
            }}
            faIconBefore={faSearch}
            filterable
            noFilteredItemsMessage={i18next.t('noAvailableOptions')}
            disableNative
            portaledDropdown
          />
          <IconButton icon={faPlus} onClick={handleAddException} />
        </FormRow>
        {otherExceptionSelected && (
          <Input
            label={text.toTitleCase(i18next.t('creditExceptions-otherInputLabel'))}
            placeholder={i18next.t('creditExceptions-otherInputPlaceholder')}
            value={inputValue}
            onChange={e => {
              setInputValue(e.target.value);
              validateOtherException(e.target.value);
            }}
            error={errors.other}
          />
        )}
        <SelectedExceptionsList
          exceptions={exceptionsList}
          onDelete={handleDeleteException}
          alreadyAdded={alreadyAdded}
        />
      </FormContainer>
    );
  }
);

export default CreditExceptionsForm;
